/**
 * 売掛・買掛・未払残高(balances.ts)の読み込み(2026-10-23 B4)。読むだけで書き込みはしない。
 * 読めるのは「確定」の権限を持つ人(経理・管理者)。入出金の記録(cash_entries)を読むため(権限ルール)
 *
 *  - 取引: 月末の月から遡って13か月分(承認済み以降 × 計上月。既存の索引 status+posting_month)
 *          + まだ支払っていないもの(承認済み・受領待ち・受領済み)は計上月を問わず全部
 *          + 立替で精算がまだのものも計上月を問わず全部
 *          + 仮払い(payment_method='prepaid')は状態・計上月を問わず全部(仮払いの後に差戻しされたものも仮払金に残すため)
 *          + 売上(type='income')は計上月を問わず全部(手入力の売上は「支払い済み」の状態で作られ、入金されるまで売掛に残るため)
 *    → 13か月より前に計上した支払い済みの経費は読まない(クレカは引き落とし日で払ったとみなすため、残高は0になっている)
 *  - 請求書: 取消以外すべて
 *  - 入出金の記録: 取引が指している記録だけを ID で読む
 */
import { collection, documentId, getDocs, query, where, type QueryDocumentSnapshot } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { PL_APPROVED_STATUSES, type CashEntry, type Invoice, type MonthStr, type Transaction } from '@core/lib/types';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

/** month を含めて n か月分(古い順) */
export function monthsBack(month: MonthStr, n: number): MonthStr[] {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const out: MonthStr[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(y, m - 1 - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` as MonthStr);
  }
  return out;
}

export const BALANCE_WINDOW_MONTHS = 13;

export async function fetchBalanceSources(month: MonthStr): Promise<{
  transactions: Transaction[];
  invoices: Invoice[];
  cashEntries: CashEntry[];
}> {
  const db = getDb();
  const tx = collection(db, 'transactions');
  const [recent, unpaid, unreimbursed, prepaid, sales, invoiceSnap] = await Promise.all([
    Promise.all(
      monthsBack(month, BALANCE_WINDOW_MONTHS).map((m) =>
        getDocs(query(tx, where('status', 'in', PL_APPROVED_STATUSES), where('posting_month', '==', m))),
      ),
    ),
    getDocs(query(tx, where('status', 'in', ['approved', 'cash_ready', 'received']))),
    getDocs(query(tx, where('advance', '==', true), where('reimbursed_at', '==', null))),
    getDocs(query(tx, where('payment_method', '==', 'prepaid'))),
    // 状態は画面側(plCategoryOf)で見る。索引を増やさないよう種類だけで絞る
    getDocs(query(tx, where('type', '==', 'income'))),
    getDocs(query(collection(db, 'invoices'), where('status', 'in', ['issued', 'partial', 'paid']))),
  ]);

  const byId = new Map<string, Transaction>();
  for (const snap of [...recent, unpaid, unreimbursed, prepaid, sales]) {
    for (const d of snap.docs) byId.set(d.id, withId<Transaction>(d));
  }
  const transactions = [...byId.values()];

  const ids = new Set<string>();
  for (const t of transactions) {
    for (const id of [t.cash_entry_id, t.prepaid_cash_entry_id, t.reimbursement_cash_entry_id]) if (id) ids.add(id);
  }
  const idList = [...ids];
  const chunks: string[][] = [];
  for (let i = 0; i < idList.length; i += 30) chunks.push(idList.slice(i, i + 30));
  const entrySnaps = await Promise.all(
    chunks.map((c) => getDocs(query(collection(db, 'cash_entries'), where(documentId(), 'in', c)))),
  );

  return {
    transactions,
    invoices: invoiceSnap.docs.map((d) => withId<Invoice>(d)),
    cashEntries: entrySnaps.flatMap((s) => s.docs.map((d) => withId<CashEntry>(d))),
  };
}
